/**
 * Application shell: fixed header, left navigation rail, scrollable work area.
 *
 * The shell owns three cross-cutting concerns:
 *  - the session probe that decides whether the workspace may render at all
 *  - tenant selection, because the header control and every page need the same one
 *  - logout, which must clear the CSRF token before the next visitor uses the tab
 *
 * Navigation shows live counts only where a query already succeeded. A count that
 * failed to load renders as "—" instead of 0, so a broken endpoint never looks like
 * an empty inbox.
 */

import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Link, Navigate, NavLink, useLocation, useNavigate } from 'react-router-dom';

import { useAlertsQuery, useJobsQuery } from '../features/queries';
import { initials } from '../lib/format';
import { useSession } from './useSession';
import { isPersistenceDegraded } from './session';
import { describeSignOutFailure, performSignOut } from './signOut';
import { useTenantContext } from '../app/useTenant';
import { Notice } from '../components/ui';
import { ToastViewport } from '../components/ui/feedback';
import { roleLabel } from '../api/types';
import type { ApiError } from '../api/errors';
import { ACTIVE_ALERT_STATUSES, NAV_GROUPS } from './constants';
import { RouteOutlet } from './RouteBoundary';
import { preloadPath } from './routePages';

function TenantSwitcher(): JSX.Element | null {
  const { tenant, tenants, selectTenant } = useTenantContext();

  if (tenants.length === 0) return null;

  if (tenants.length === 1) {
    const only = tenants[0];
    if (only === undefined) return null;
    return (
      <div className="tenant-picker">
        <span className="faint">租户</span>
        <span className="nowrap" style={{ fontWeight: 500 }}>
          {only.name}
        </span>
        <span className="badge badge--neutral">{roleLabel(only.role)}</span>
      </div>
    );
  }

  return (
    <div className="tenant-picker">
      <label className="faint" htmlFor="tenant-select">
        租户
      </label>
      <select
        id="tenant-select"
        value={tenant?.id ?? ''}
        onChange={(event) => {
          selectTenant(event.target.value);
        }}
      >
        {tenant === null ? <option value="">请选择租户</option> : null}
        {tenants.map((entry) => (
          <option key={entry.id} value={entry.id}>
            {entry.name}（{roleLabel(entry.role)}）
          </option>
        ))}
      </select>
    </div>
  );
}

export interface AppShellProps {
  /** Rendered above the work area when the session probe could not reach the API. */
  readonly probeWarning: string | null;
}

export function AppShell({ probeWarning }: AppShellProps): JSX.Element {
  const session = useSession();
  const { tenant, needsSelection } = useTenantContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [signOutError, setSignOutError] = useState<ApiError | null>(null);
  const [signingOut, setSigningOut] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const navToggleRef = useRef<HTMLButtonElement>(null);

  // Close the drawer and the user menu whenever the route changes.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setNavOpen(false);
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointer = (event: MouseEvent): void => {
      if (menuRef.current !== null && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    // Keyboard users land on the first entry instead of having to tab into the panel.
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!navOpen) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setNavOpen(false);
        navToggleRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [navOpen]);

  /** Arrow keys move between menu entries; Tab leaves the menu and closes it. */
  const onMenuKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Tab') {
      setMenuOpen(false);
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const items = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])'),
    );
    const index = items.indexOf(document.activeElement as HTMLElement);
    const step = event.key === 'ArrowDown' ? 1 : -1;
    items[(index + step + items.length) % items.length]?.focus();
  };

  const tenantId = tenant?.id ?? null;
  // Read once per render: if storage was blocked, the operator is told that the
  // session will not survive a refresh instead of discovering it by surprise.
  const persistenceAvailable = !isPersistenceDegraded();

  // Navigation counters reuse the same cache entries the pages use, so opening a
  // page does not trigger a second request for data already on screen.
  const alertsQuery = useAlertsQuery({ tenantId, limit: 50 });
  const jobsQuery = useJobsQuery({ tenantId, limit: 50 });

  const counters: Record<'alerts' | 'jobs', string> = {
    // The badge counts rows on the loaded page, which is why a failed query shows
    // "—" rather than 0 and why the label never claims to be a total.
    alerts: alertsQuery.isSuccess
      ? String(
          alertsQuery.data.items.filter((item) => ACTIVE_ALERT_STATUSES.includes(item.status))
            .length,
        )
      : '—',
    jobs: jobsQuery.isSuccess
      ? String(
          jobsQuery.data.items.filter(
            (item) =>
              item.status === 'queued' || item.status === 'leased' || item.status === 'running',
          ).length,
        )
      : '—',
  };

  const displayName = session.user?.display_name.trim() ?? '';
  const email = session.user?.email ?? '';
  const avatarSource = displayName === '' ? email : displayName;

  /**
   * Ends the session on the server first, then locally.
   *
   * The navigation happens either way — the operator asked to leave and local
   * credentials are cleared regardless — but a failed revocation is *shown* after
   * the redirect so nobody is told the session ended when it may not have. The
   * message travels in router state because this component unmounts on navigation.
   */
  const signOut = (): void => {
    setSigningOut(true);
    void performSignOut().then((result) => {
      setSigningOut(false);
      if (result.outcome === 'local-only') {
        setSignOutError(result.error);
      }
      void navigate('/login', {
        replace: true,
        state:
          result.outcome === 'local-only'
            ? { signOutNotice: describeSignOutFailure(result.error) }
            : undefined,
      });
    });
  };

  return (
    <div className="shell">
      <a className="skip-link" href="#main-content">
        跳到主要内容
      </a>
      <div className="shell__brand">
        <span className="shell__brand-mark" aria-hidden="true">
          YQ
        </span>
        <span className="shell__brand-text">舆情监测工作台</span>
      </div>

      <header className="shell__header">
        <button
          type="button"
          ref={navToggleRef}
          className="btn btn--sm shell__nav-toggle"
          aria-expanded={navOpen}
          aria-controls="shell-nav"
          onClick={() => {
            setNavOpen((open) => !open);
          }}
        >
          <span aria-hidden="true">☰</span>
          <span className="sr-only">{navOpen ? '收起导航' : '展开导航'}</span>
        </button>
        <TenantSwitcher />
        <div className="shell__header-spacer" />
        <div className="user-menu" ref={menuRef}>
          <button
            ref={menuButtonRef}
            type="button"
            className="user-chip"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => {
              setMenuOpen((open) => !open);
            }}
          >
            <span className="user-chip__avatar" aria-hidden="true">
              {initials(avatarSource)}
            </span>
            <span className="user-chip__text">
              <span style={{ display: 'block', fontSize: 12.5, fontWeight: 500 }}>
                {displayName === '' ? email : displayName}
              </span>
              <span className="user-chip__email" title={email}>
                {email}
              </span>
            </span>
            <span aria-hidden="true" className="faint">
              ▾
            </span>
          </button>
          {menuOpen ? (
            <div
              className="user-menu__panel"
              role="menu"
              aria-label="账户"
              onKeyDown={onMenuKeyDown}
            >
              <Link role="menuitem" className="user-menu__item" to="/account/password">
                修改密码
              </Link>
              <button
                type="button"
                role="menuitem"
                className="user-menu__item"
                onClick={signOut}
                disabled={signingOut}
                aria-busy={signingOut}
              >
                {signingOut ? '退出中…' : '退出登录'}
              </button>
            </div>
          ) : null}
        </div>
      </header>

      {navOpen ? (
        <button
          type="button"
          className="shell__nav-backdrop"
          aria-label="关闭导航"
          onClick={() => {
            setNavOpen(false);
          }}
        />
      ) : null}
      <nav
        id="shell-nav"
        className={
          navOpen
            ? 'shell__nav shell__nav--drawer shell__nav--open'
            : 'shell__nav shell__nav--drawer'
        }
        aria-label="主导航"
      >
        {session.user?.is_platform_operator ? (
          <NavLink to="/platform" className="shell__nav-link">
            平台管理台
          </NavLink>
        ) : null}
        {NAV_GROUPS.map((group) => (
          <div
            className="shell__nav-section"
            key={group.title}
            role="group"
            aria-label={group.title}
          >
            <div className="shell__nav-group" aria-hidden="true">
              {group.title}
            </div>
            {group.items.map((item) => {
              const counterKey = item.counter;
              const count = counterKey !== undefined ? counters[counterKey] : undefined;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  onPointerEnter={() => {
                    preloadPath(item.to);
                  }}
                  onFocus={() => {
                    preloadPath(item.to);
                  }}
                  className={({ isActive }) =>
                    isActive ? 'shell__nav-link shell__nav-link--active' : 'shell__nav-link'
                  }
                >
                  {item.label}
                  {count !== undefined ? (
                    <span
                      className="shell__nav-count num"
                      aria-label={`${item.label}待处理 ${count} 条`}
                    >
                      {count}
                    </span>
                  ) : null}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      <main className="shell__main" id="main-content" tabIndex={-1}>
        {signOutError !== null ? (
          <div style={{ marginBottom: 12 }}>
            <Notice
              tone="error"
              title="服务端退出失败"
              detail={describeSignOutFailure(signOutError)}
            />
          </div>
        ) : null}

        {!persistenceAvailable ? (
          <div style={{ marginBottom: 12 }}>
            <Notice
              tone="warn"
              title="浏览器存储不可用"
              detail="当前浏览器禁止本站使用本地存储，刷新页面后需要重新登录，租户选择也不会保留。"
            />
          </div>
        ) : null}

        {probeWarning !== null ? (
          <div style={{ marginBottom: 12 }}>
            <Notice tone="warn" title="未能确认登录状态" detail={probeWarning} />
          </div>
        ) : null}

        {needsSelection ? (
          <div style={{ marginBottom: 12 }}>
            <Notice
              tone="info"
              title="请先选择租户"
              detail="选择租户后可检索公共内容与本租户私有内容，主题、告警、任务与报告仍按租户隔离。"
            />
          </div>
        ) : null}

        {/* Keyed by path so each navigation replays the page entry transition. */}
        <div className="page-transition" key={location.pathname}>
          <RouteOutlet />
        </div>
      </main>
      <ToastViewport />
    </div>
  );
}

/**
 * Guards the authenticated area.
 *
 * `unavailable` deliberately still renders the shell: if the API is down or an
 * endpoint is missing, each page shows its own explicit error, which is more
 * truthful than a login screen implying the credentials were rejected.
 *
 * The attempted location travels in router state so that signing in returns the
 * user to the page they actually asked for instead of dropping them on the
 * dashboard.
 */
export function RequireSession({ children }: { readonly children: JSX.Element }): JSX.Element {
  const session = useSession();
  const location = useLocation();

  if (session.status !== 'authenticated') {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return children;
}
